import React, { useEffect, useReducer, useState } from 'react';

import arrayMove from 'array-move';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import personnelInformationReducer, {
  initialState,
  mapDispatch,
} from 'reducers/personnelInformationReducer';

import RowBar from 'components/application/RowBar';
import SuccessToast from 'components/application/SuccessToast';

import AddButton from 'components/buttons/AddButton';
import { useBreadBoard } from 'components/contexts/Toaster';
import FieldSidePanel from 'components/settings/personnel/information/FieldSidePanel';
import FieldTable from 'components/settings/personnel/information/FieldTable';
import UpdateConfirmationModal from 'components/settings/personnel/information/UpdateConfirmationModal';

import { resourceIdsAsPositionParams } from 'components/helpers/ordering';
import { fieldSettingAttributes } from 'components/helpers/resources/fieldSettings';

export default function InformationSection({ initialFieldSettings }) {
  const breadBoard = useBreadBoard();

  const [state, dispatch] = useReducer(
    personnelInformationReducer,
    initialState,
  );

  const actions = mapDispatch(dispatch);

  const [
    fieldSettingUpdateConfirmationModal,
    setFieldSettingUpdateConfirmationModal,
  ] = useState({ isOpen: false, destroyFieldValueCount: 0 });

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.personnel.InformationSection',
  });

  const { t: i18nTSettings } = useTranslation('common', {
    keyPrefix: 'settings',
  });

  const formattedRows = state.domain.fieldSettingCollection.allDataIds.map(
    (fieldSettingId) => {
      const fieldSetting =
        state.domain.fieldSettingCollection.data[fieldSettingId];

      const fieldAttribute =
        state.domain.fieldAttributeCollection.data[
          fieldSetting.relationships.fieldAttribute.data.id
        ];

      return {
        fieldSetting: { id: fieldSetting.id, ...fieldSetting.attributes },
        fieldAttribute: {
          id: fieldAttribute.id,
          ...fieldAttribute.attributes,
          fieldValueCount: fieldAttribute.meta.fieldValueCount,
        },
      };
    },
  );

  const createFieldSetting = () => {
    actions.startSubmittingForm();

    axios
      .post(
        `/custom_fields/field_settings`,
        fieldSettingAttributes(state.application.form),
      )
      .then((response) => {
        const fieldAttribute = response.data.included.find(
          (inclusion) => inclusion.type === 'fieldAttribute',
        );

        actions.appendFieldSetting({ data: response.data });

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='shared.success.added'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: fieldAttribute.attributes.name }}
              />
            }
          />,
        );
      })
      .catch((error) =>
        error.response.status === 422 ?
          actions.setValidationErrors({ data: error.response.data })
        : actions.setFallbackError(),
      );
  };

  const confirmUpdateFieldSetting = () => {
    actions.startSubmittingForm();

    axios
      .post(
        `/custom_fields/field_settings/${state.application.form.fieldSettingId}/update_confirmation`,
        fieldSettingAttributes(state.application.form),
      )
      .then((response) => {
        const { destroyFieldValueCount, required: isConfirmationRequired } =
          response.data.data.attributes;
        isConfirmationRequired ?
          setFieldSettingUpdateConfirmationModal({
            destroyFieldValueCount,
            isOpen: true,
          })
        : updateFieldSetting();
      })
      .catch((_error) => actions.setFallbackError());
  };

  const updateFieldSetting = () => {
    axios
      .patch(
        `/custom_fields/field_settings/${state.application.form.fieldSettingId}`,
        fieldSettingAttributes(state.application.form),
      )
      .then((response) => {
        const fieldAttribute = response.data.included.find(
          (inclusion) => inclusion.type === 'fieldAttribute',
        );

        actions.updateFieldSetting({ data: response.data });

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='shared.success.updated'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: fieldAttribute.attributes.name }}
              />
            }
          />,
        );
      })
      .catch((error) =>
        error.response.status === 422 ?
          actions.setValidationErrors({ data: error.response.data })
        : actions.setFallbackError(),
      );
  };

  const destroyFieldAttribute = ({ fieldAttribute, fieldSetting }) => {
    axios
      .delete(`/custom_fields/field_attributes/${fieldAttribute.id}`)
      .then((_response) => {
        actions.deleteFieldSetting({ id: fieldSetting.id });

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='settings.personnel.InformationSection.destroyFieldAttribute.success'
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: fieldAttribute.name }}
              />
            }
          />,
        );
      });
  };

  const destroyFieldValues = ({ fieldAttribute }) => {
    axios
      .post(
        `/custom_fields/field_attributes/${fieldAttribute.id}/field_values_bulk_destroy`,
      )
      .then((_response) => {
        actions.zeroFieldValueCount({ id: fieldAttribute.id });

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='settings.personnel.InformationSection.destroyFieldValues.success'
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ name: fieldAttribute.name }}
              />
            }
          />,
        );
      });
  };

  const updateFieldSettingIncluded = ({ fieldSetting }) => {
    axios
      .patch(`/custom_fields/field_settings/${fieldSetting.id}`, {
        field_setting: { included: !fieldSetting.included },
      })
      .then((response) => actions.updateFieldSetting({ data: response.data }))
      .catch(breadBoard.addInedibleToast);
  };

  const updateFieldSettingRestricted = ({ fieldSetting }) => {
    axios
      .patch(`/custom_fields/field_settings/${fieldSetting.id}`, {
        field_setting: { restricted: !fieldSetting.restricted },
      })
      .then((response) => actions.updateFieldSetting({ data: response.data }))
      .catch(breadBoard.addInedibleToast);
  };

  const updateFieldSettingsOrder = ({ newIndex, oldIndex }) => {
    if (oldIndex !== newIndex) {
      const orderedFieldSettingIds = arrayMove(
        state.domain.fieldSettingCollection.allDataIds,
        oldIndex,
        newIndex,
      );

      actions.reorderFieldSettings({ ids: orderedFieldSettingIds });
      axios
        .post(`/custom_fields/field_settings_bulk_update`, {
          field_settings_bulk_update: {
            field_settings_attributes: resourceIdsAsPositionParams(
              orderedFieldSettingIds,
            ),
          },
        })
        .catch(breadBoard.addInedibleToast);
    }
  };

  useEffect(() => actions.loadData({ data: initialFieldSettings }), []);

  return (
    <>
      <h3 className='tw-mb-6 tw-mt-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
        {i18nT('title')}
      </h3>
      <p className='tw-mb-5'>{i18nT('description')}</p>
      <RowBar
        actions={
          <AddButton
            onClick={actions.openNewSidePanel}
            text={i18nTSettings('addCustomField')}
          />
        }
        modifiers={['border-top-curved', 'large']}
      />
      <FieldTable
        formattedRows={formattedRows}
        onDestroyFieldAttribute={destroyFieldAttribute}
        onDestroyFieldValues={destroyFieldValues}
        onEdit={actions.openEditSidePanel}
        onIncludedToggle={updateFieldSettingIncluded}
        onRestrictedToggle={updateFieldSettingRestricted}
        onSortEnd={updateFieldSettingsOrder}
      />
      <FieldSidePanel
        closeCallback={actions.resetSidePanel}
        form={state.application.form}
        onCreate={createFieldSetting}
        onFormOptionAppend={actions.appendFormOption}
        onFormOptionDelete={actions.deleteFormOption}
        onFormOptionOrderChange={actions.updateFormOptionOrder}
        onFormOptionValueChange={actions.updateFormOptionValue}
        onFormValueChange={actions.updateFormValue}
        onUpdate={confirmUpdateFieldSetting}
        removeErrorStyling={actions.removeErrorStyling}
        sidePanel={state.ui.sidePanel}
      />
      <UpdateConfirmationModal
        destroyFieldValueCount={
          fieldSettingUpdateConfirmationModal.destroyFieldValueCount
        }
        isOpen={fieldSettingUpdateConfirmationModal.isOpen}
        onUpdate={updateFieldSetting}
        setClose={() => {
          setFieldSettingUpdateConfirmationModal({
            destroyFieldValueCount: 0,
            isOpen: false,
          });

          actions.stopSubmittingForm();
        }}
      />
    </>
  );
}

InformationSection.propTypes = {
  initialFieldSettings: PropTypes.exact({
    data: PropTypes.array.isRequired,
    included: PropTypes.array,
  }).isRequired,
};
